import { useDrag, useDrop } from 'react-dnd'
import type {
  ConnectDragSource,
  ConnectDropTarget,
  DragLayerMonitor,
  DropTargetMonitor,
} from 'react-dnd'

import type moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'

import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { Shift, ShiftsRow, UnassignedShift } from 'modules/shift_management/lib/types'

// The file acts as a wrapper of react-dnd in case we modify it it in the future
// All methods use the name with shifts as we are going to only permit these
// elements to be drag&drop

export type DropActions = 'copy' | 'move'
const ELEMENT_TYPES = Object.freeze({
  shift: 'shift',
  unassignedShift: 'unassignedShift',
})

export interface DragItem {
  shiftsRow?: ShiftsRow
  date: string
  shift?: Shift
  unassignedShift?: UnassignedShift
}

export interface DropUnassignedItem {
  shiftsRow: ShiftsRow
  date: string
}

interface DragOptions {
  dropEffect: string
}

interface UseDragShiftProps {
  shiftsRow?: ShiftsRow
  shift?: Shift
  unassignedShift?: UnassignedShift
  options: DragOptions
}

type CollectedDragProps = { isDragging: boolean }

type UseDragShiftReturn = {
  isDragging: boolean
  drag: ConnectDragSource
}

export const useDragShift = ({
  shiftsRow,
  shift,
  unassignedShift,
  options,
}: UseDragShiftProps): UseDragShiftReturn => {
  const [{ isDragging }, drag] = useDrag<DragItem, void, CollectedDragProps>(() => ({
    type: shift ? ELEMENT_TYPES.shift : ELEMENT_TYPES.unassignedShift,
    item: {
      shiftsRow: shiftsRow,
      shift: shift,
      unassignedShift: unassignedShift,
      date: shift
        ? dateToString(shift.date, 'backend')
        : dateToString(unassignedShift!.startAt, 'backend'),
    },
    options,
    collect: (monitor: DragLayerMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }))

  return { isDragging, drag }
}

interface UseDropShiftProps {
  shiftsRow: ShiftsRow
  date: moment.Moment
  shift?: Shift
  action?: (item: DragItem) => void
}

type CollectedDropProps = {
  isOver: boolean
  isOverCurrent: boolean
  canDrop: boolean
  originalItem: DragItem
}

type UseDropShiftReturn = {
  isOver: boolean
  isOverCurrent: boolean
  canDrop: boolean
  originalItem: DragItem
  drop: ConnectDropTarget
}

export const useDropShift = ({
  shiftsRow,
  date,
  shift,
  action,
}: UseDropShiftProps): UseDropShiftReturn => {
  const [{ view }] = useSchedule()

  const [{ isOver, isOverCurrent, canDrop, originalItem }, drop] = useDrop<
    DragItem,
    void,
    CollectedDropProps
  >(() => ({
    accept: [ELEMENT_TYPES.shift, ELEMENT_TYPES.unassignedShift],
    canDrop: (item: DragItem) => {
      const formattedDate = dateToString(date, 'backend')

      if (item.unassignedShift) {
        return !!shiftsRow.key && item.date === formattedDate
      }
      return (
        item.shiftsRow?.key !== shiftsRow.key ||
        (view.isDaily && shift?.id !== item.shift!.id) ||
        item.date !== formattedDate
      )
    },
    drop: (item: DragItem) => {
      action ? action(item) : null
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: !!monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true }),
      canDrop: !!monitor.canDrop(),
      originalItem: monitor.getItem(),
    }),
  }))

  return { isOver, isOverCurrent, canDrop, originalItem, drop }
}
